import React, { useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { Modal, Button, Table, Form, Alert ,Spinner} from 'react-bootstrap';
import moment from 'moment';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import Swal from 'sweetalert2';
import ViewVisitorRequest from './ViewVisitorRequest';

// DTOs
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface GetVisitorsResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO[];
  total: number;
}

interface ApprovalPayload {
  visitorRequestId: number;
  approvalStatus: 'APPROVED' | 'REJECTED';
  approvalComment: string;
}

interface PendingModuleProps {
  show: boolean;
  onHide: () => void;
}

const statusColors: { [key: string]: string } = {
  PENDING: 'badge bg-secondary',
};

const PendingModule: React.FC<PendingModuleProps> = ({ show, onHide }) => {
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState<ApprovalPayload>({
    visitorRequestId: 0,
    approvalStatus: 'APPROVED',
    approvalComment: '',
  });
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const fetchVisitorRequests = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiDefinitions.getPendingApprovalVisitors('1');
        if (res.status === 200) {
          const data: GetVisitorsResponse = res.data;
          setVisitorRequests(data.data || []);
          setTotal(data.total || 0);
        } else {
          setError(res.data?.statusDescription || 'Failed to fetch pending visitor requests');
        }
      } catch (err: any) {
      } finally {
        setLoading(false);
      }
    },
    [dateFilter]
  );

  useEffect(() => {
    if (show) {
      setPageNo(1); // Reset to page 1 when modal opens
      fetchVisitorRequests(1);
    }
  }, [show, dateFilter, fetchVisitorRequests]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(total / 15)) {
      setPageNo(newPage);
      fetchVisitorRequests(newPage);
    }
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1); // Reset to page 1 on date change
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  const handleApproveClick = (visitorRequestId: number) => {
    if (!checkPermission('ROLE_APPROVE_VISITOR_REQUEST')) {
      Swal.fire({
        icon: 'error',
        title: 'Permission Denied',
        text: 'You do not have permission to approve visitor requests.',
      });
      return;
    }
    setApprovalForm({
      visitorRequestId,
      approvalStatus: 'APPROVED',
      approvalComment: '',
    });
    setApprovalError(null);
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkPermission('ROLE_APPROVE_VISITOR_REQUEST')) {
      setApprovalError('You do not have permission to approve visitor requests.');
      return;
    }
    if (!approvalForm.approvalComment.trim()) {
      setApprovalError('Please provide an approval comment.');
      return;
    }

    try {
      setLoading(true);
      const res: AxiosResponse = await apiDefinitions.approveVisitor(approvalForm);
      if (res.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Visitor request ${approvalForm.approvalStatus.toLowerCase()} successfully.`,
        });
        setShowApprovalModal(false);
        fetchVisitorRequests(pageNo); // Refresh the list
      } else {
        setApprovalError(res.data?.statusDescription || 'Failed to process approval');
      }
    } catch (err: any) {
      setApprovalError(err.response?.data?.statusDescription || 'Failed to process approval. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Pending Visitor Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Filter by Date</label>
              <input
                type="date"
                className="form-control"
                value={dateFilter}
                onChange={handleDateFilterChange}
                disabled={loading}
              />
            </div>
          </div>

          {loading && (
            <div className="text-center">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && visitorRequests.length === 0 && (
            <div className="alert alert-info">No pending visitor requests found.</div>
          )}

          {!loading && !error && visitorRequests.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Visitor Name</th>
                    <th>Company</th>
                    <th>Arrival Time</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitorRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.id}</td>
                      <td>{request.visitors[0]?.name || 'N/A'}</td>
                      <td>{request.visitors[0]?.visitorCompany || 'N/A'}</td>
                      <td>
                        {request.visitors[0]?.arrivalTime
                          ? moment(request.visitors[0].arrivalTime).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className={statusColors[request.status] || 'badge bg-secondary'}>
                          {request.status || 'N/A'}
                        </span>
                      </td>
                      <td>{moment(request.dateTime).format('YYYY-MM-DD HH:mm')}</td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="icon btn-secondary btn-sm"
                            title="View Request"
                            onClick={() => handleViewClick(request.id)}
                          >
                            <i className="ti ti-eye" />
                          </Button>
                          <Button
                            variant="icon btn-success btn-sm"
                            title="Approve/Reject Request"
                            onClick={() => handleApproveClick(request.id)}
                            disabled={!checkPermission('ROLE_APPROVE_VISITOR_REQUEST')}
                          >
                            <i className="ti ti-check" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-primary"
              disabled={pageNo === 1 || loading}
              onClick={() => handlePageChange(pageNo - 1)}
            >
              Previous
            </Button>
            <span>
              Page {pageNo} of {Math.ceil(total / 15) || 1}
            </span>
            <Button
              variant="outline-secondary"
              disabled={pageNo >= Math.ceil(total / 15) || loading}
              onClick={() => handlePageChange(pageNo + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approval Modal */}
      <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Approve/Reject Visitor Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleApprovalSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Approval Status</Form.Label>
              <Form.Select
                value={approvalForm.approvalStatus}
                onChange={(e) =>
                  setApprovalForm({ ...approvalForm, approvalStatus: e.target.value as 'APPROVED' | 'REJECTED' })
                }
                disabled={loading}
              >
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Approval Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={approvalForm.approvalComment}
                onChange={(e) => setApprovalForm({ ...approvalForm, approvalComment: e.target.value })}
                placeholder="Enter your comment (e.g., Approved by HOD)"
                disabled={loading}
                required
              />
            </Form.Group>
            {approvalError && <Alert variant="danger">{approvalError}</Alert>}
            <div className="d-flex justify-content-end">
              <Button variant="primary" onClick={() => setShowApprovalModal(false)} className="me-2" disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View Modal */}
      {selectedVisitorRequestId && (
        <ViewVisitorRequest
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          visitorRequestId={selectedVisitorRequestId}
        />
      )}
    </>
  );
};

export default PendingModule;